import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Download,
  Eye,
  Pencil,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { paymentService } from '../../services/paymentService.js';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
} from '../common/index.js';

export default function PaymentList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedShipmentDetails, setSelectedShipmentDetails] = useState(null);
  const [filterValues, setFilterValues] = useState({
    search: '',
    filter: 'all',
    userFilter: '',
    fromDate: '',
    toDate: '',
  });
  const perPage = 10;

  const isAdminRole =
    user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant';
  const isAgent = user?.role === 'Agent';

  useEffect(() => {
    if (isAdminRole) {
      fetchUsers();
    }
  }, [isAdminRole]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchPayments(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues.filter, filterValues.userFilter, filterValues.fromDate, filterValues.toDate]);

  useEffect(() => {
    fetchPayments(currentPage);
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      const response = await paymentService.getDistinctUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPayments = async (page = currentPage) => {
    try {
      setLoading(true);
      let response;

      const params = {
        page,
        per_page: perPage,
        ...(filterValues.fromDate && { from_date: filterValues.fromDate }),
        ...(filterValues.toDate && { to_date: filterValues.toDate }),
        ...(filterValues.userFilter && { user_id: filterValues.userFilter }),
      };

      if (filterValues.filter === 'pending') {
        response = await paymentService.getPendingPayments(params);
        setPayments(response.data.data || []);
      } else {
        response = await paymentService.getAllPaymentHistory(params);
        setPayments(response.data.data || []);
      }
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching payments:', error);
      Alert.error(error.response?.data?.error || 'Failed to fetch payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (payment) => {
    const confirmed = await Alert.confirm(
      `Delete payment record of ₹${payment.payment_amount?.toFixed(2)} for shipment ${payment.shipment_id}?`,
      'Delete Payment Record'
    );

    if (!confirmed) return;

    try {
      await paymentService.deletePayment(payment.id);
      Alert.success('Payment record deleted successfully');
      fetchPayments();
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to delete payment');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      const params = new URLSearchParams({
        ...(filterValues.fromDate && { from_date: filterValues.fromDate }),
        ...(filterValues.toDate && { to_date: filterValues.toDate }),
        ...(filterValues.userFilter && { user_id: filterValues.userFilter }),
      });

      const response = await paymentService.exportPaymentHistoryCSV(params.toString());

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_history_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      Alert.success('Payment history exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      Alert.error('Failed to export payment history');
    } finally {
      setExporting(false);
    }
  };

  const handleViewShipmentPayments = async (shipmentId) => {
    try {
      const response = await paymentService.getShipmentPaymentDetails(shipmentId);
      setSelectedShipmentDetails(response.data.data);
      setShowPaymentModal(true);
    } catch (error) {
      Alert.error('Failed to load shipment payment details');
    }
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : 'N/A';

  const filteredPayments = payments.filter((payment) =>
    payment.shipment_id?.toLowerCase().includes(filterValues.search.toLowerCase())
  );

  const filterConfig = [
    {
      key: 'search',
      label: 'Search',
      placeholder: 'Search by shipment ID...',
    },
    ...(isAdminRole
      ? [
          {
            key: 'filter',
            label: 'Payment Type',
            type: 'select',
            options: [
              { value: 'all', label: 'All Payments' },
              { value: 'pending', label: 'Pending Payments' },
            ],
          },
        ]
      : []),
    { key: 'fromDate', label: 'From Date', type: 'date' },
    { key: 'toDate', label: 'To Date', type: 'date' },
    ...(isAdminRole && users.length > 0
      ? [
          {
            key: 'userFilter',
            label: 'Agent',
            type: 'select',
            options: users.map((u) => ({
              value: u.user_id,
              label: `${u.name} (${u.user_id})`,
            })),
          },
        ]
      : []),
  ];

  const columns = [
    {
      key: 'shipment_id',
      label: 'Shipment ID',
      render: (payment) => (
        <button
          type="button"
          onClick={() => handleViewShipmentPayments(payment.shipment_id)}
          className="font-medium text-brand hover:underline"
        >
          {payment.shipment_id}
        </button>
      ),
    },
    {
      key: 'payment_amount',
      label: 'Amount',
      render: (payment) => `₹${payment.payment_amount?.toFixed(2) || '0.00'}`,
    },
    {
      key: 'payment_mode',
      label: 'Payment Mode',
      render: (payment) => (
        <span className="uppercase text-xs">
          {payment.payment_mode?.replace(/_/g, ' ') || 'N/A'}
        </span>
      ),
    },
    {
      key: 'previous_balance',
      label: 'Pre. Balance',
      render: (payment) => `₹${payment.previous_balance?.toFixed(2) || '0.00'}`,
    },
    {
      key: 'new_balance',
      label: 'New Balance',
      render: (payment) => `₹${payment.new_balance?.toFixed(2) || '0.00'}`,
    },
    {
      key: 'payment_date',
      label: 'Date',
      render: (payment) => formatDate(payment.payment_date),
    },
    {
      key: 'recorded_by_name',
      label: 'Recorded By',
      render: (payment) => payment.recorded_by_name || 'N/A',
    },
    {
      key: 'remarks',
      label: 'Remarks',
      cellClassName: 'max-w-[160px] truncate',
      render: (payment) => payment.remarks || 'N/A',
    },
  ];

  const getActions = (payment) => [
    {
      label: 'Edit Payment',
      icon: Pencil,
      onClick: () => navigate(`/payment-history/edit/${payment.id}`),
    },
    {
      label: 'View Shipment',
      icon: Eye,
      onClick: () => navigate(`/shipments/view/${payment.shipment_id}`),
    },
    {
      label: 'Delete Payment',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(payment),
    },
  ];

  return (
    <PageLayout
      title="Payment History"
      icon={CreditCard}
      maxWidth="max-w-full"
      actions={
        <>
          <Button
            variant="outline"
            icon={TrendingUp}
            onClick={() => navigate('/daily-earnings')}
          >
            Daily Earnings
          </Button>
          <Button
            icon={Download}
            onClick={handleExport}
            disabled={exporting || payments.length === 0}
            loading={exporting}
          >
            Export CSV
          </Button>
        </>
      }
    >
      <FilterPanel
        filters={filterConfig}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() =>
          setFilterValues({
            search: '',
            filter: 'all',
            userFilter: '',
            fromDate: '',
            toDate: '',
          })
        }
      />

      <DataTable
        columns={columns}
        data={filteredPayments}
        loading={loading}
        emptyMessage="No payments found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {showPaymentModal && selectedShipmentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card shadow-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-base sm:text-lg font-semibold text-secondary">
                Shipment Payment History
              </h2>
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700 text-3xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <div className="bg-gray-50 rounded-card p-4 mb-6">
                <h3 className="text-base font-semibold text-secondary mb-3 pb-2 border-b border-gray-200">
                  Shipment Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Shipment ID:</span>
                    <span className="font-bold text-brand ml-2">
                      {selectedShipmentDetails.shipment.shipment_id}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Sender:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.sender_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Receiver:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.receiver_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Agent:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.agent_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-bold text-brand ml-2">
                      ₹{selectedShipmentDetails.shipment.total_amount?.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Paid Amount:</span>
                    <span className="font-bold text-green-600 ml-2">
                      ₹{selectedShipmentDetails.shipment.paid_amount?.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Balance:</span>
                    <span className="font-bold text-red-600 ml-2">
                      ₹{selectedShipmentDetails.shipment.balance_amount?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-secondary mb-3">Payment History</h3>
                {selectedShipmentDetails.payment_history.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No payment records found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">Date</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">Amount</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">Mode</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">Previous Balance</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">New Balance</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">Recorded By</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-700">Remarks</th>
                          {!isAgent && (
                            <th className="px-4 py-2 text-left font-bold text-gray-700">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedShipmentDetails.payment_history.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">{formatDate(payment.payment_date)}</td>
                            <td className="px-4 py-2 font-bold text-green-600">
                              ₹{payment.payment_amount?.toFixed(2)}
                            </td>
                            <td className="px-4 py-2 uppercase text-xs">
                              {payment.payment_mode?.replace(/_/g, ' ')}
                            </td>
                            <td className="px-4 py-2">₹{payment.previous_balance?.toFixed(2)}</td>
                            <td className="px-4 py-2 font-bold text-red-600">
                              ₹{payment.new_balance?.toFixed(2)}
                            </td>
                            <td className="px-4 py-2">{payment.recorded_by_name}</td>
                            <td className="px-4 py-2 text-gray-600">{payment.remarks || 'N/A'}</td>
                            {!isAgent && (
                              <td className="px-4 py-2">
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={Pencil}
                                    onClick={() => {
                                      setShowPaymentModal(false);
                                      navigate(`/payment-history/edit/${payment.id}`);
                                    }}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={Trash2}
                                    onClick={() => handleDelete(payment)}
                                    className="text-red-600 hover:bg-red-50"
                                  />
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/shipments/view/${selectedShipmentDetails.shipment.shipment_id}`)
                  }
                >
                  View Shipment Details
                </Button>
                <Button onClick={() => setShowPaymentModal(false)}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
